import React from "react";
import Select from "react-select";
import { OptionTypeBase } from "react-select";
import { ValueType } from "react-select";
import FormHelperText from "@material-ui/core/FormHelperText";
import { getIn, FieldProps, useField, useFormikContext } from "formik";

import { ISelectOption } from "types";

import {
  Control,
  DropdownIndicator,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
} from "./components";
import { useStyles } from "./styles";

const standardComponents = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
  DropdownIndicator,
  IndicatorSeparator: null,
};

interface IProps extends OptionTypeBase {
  label?: React.ReactNode;
}
type SelectFieldFormikProps = IProps & FieldProps;

interface Option {
  label: string;
  value: string;
}

export const FormikSelectField: React.FunctionComponent<IProps> = ({
  onChange,
  components,
  options,
  classes,
  isClearable = false,
  isMulti = false,
  isDisabled = false,
  margin = "dense",
  name,
  helperText,
}: IProps) => {
  const customClasses = useStyles({ margin, isDisabled, isMulti });
  const mergedComponents = {
    ...standardComponents,
    ...components,
  };
  const mergedClasses = {
    ...customClasses,
    ...classes,
  };

  const { errors, touched, setFieldValue, setTouched } = useFormikContext();
  const [field] = useField(name);

  const fieldError = getIn(errors, name);
  const showError = getIn(touched, name) && !!fieldError;
  const showHelperText = !showError && !!helperText;

  const getMultiSelectValue = () =>
    options.filter((option: ISelectOption) =>
      field.value.includes(option.value)
    );

  const getSingleValue = () => {
    if (field.value) {
      return options.find((option: any) => option.value === field.value);
    }

    if (options?.length === 0) {
      return null;
    }

    if (options?.length === 1) {
      return options[0];
    }

    return options?.find(
      (option: ISelectOption) => option.value === field.value
    );
  };

  const onChangeFunction = (option: ValueType<Option | Option[]>) => {
    const fieldValue = isMulti
      ? (option as Option[]).map((item: Option) => item.value)
      : (option as Option).value;

    return onChange
      ? onChange(fieldValue)
      : setFieldValue(field.name, fieldValue);
  };

  const onBlurFunction = () =>
    setTouched({
      ...touched,
      [field.name]: true,
    });

  return (
    <>
      <Select
        classes={mergedClasses}
        options={options}
        name={field.name}
        components={mergedComponents}
        value={isMulti ? getMultiSelectValue() : getSingleValue()}
        onBlur={onBlurFunction}
        onChange={onChangeFunction}
        placeholder=""
        showError={showError}
        isClearable={isClearable}
        isMulti={isMulti}
        isDisabled={isDisabled}
        margin={margin}
      />
      {showHelperText && (
        <FormHelperText className={customClasses.helperText}>
          {helperText}
        </FormHelperText>
      )}
      {showError && (
        <FormHelperText error className={customClasses.helperText}>
          {fieldError}
        </FormHelperText>
      )}
    </>
  );
};
